import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CampaignDashboard = ({ campaigns, onRefresh }) => {
  const activeCampaigns = campaigns?.filter(c => c?.status === 'active') || [];
  const draftCampaigns = campaigns?.filter(c => c?.status === 'draft') || [];
  const completedCampaigns = campaigns?.filter(c => c?.status === 'completed') || [];

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: 'Facebook',
      instagram: 'Instagram',
      whatsapp: 'MessageCircle',
      linkedin: 'Linkedin'
    };
    return icons?.[platform] || 'Share2';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      facebook: 'text-blue-600',
      instagram: 'text-pink-600',
      whatsapp: 'text-green-600',
      linkedin: 'text-blue-700'
    };
    return colors?.[platform] || 'text-primary';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      completed: 'bg-blue-100 text-blue-800',
      paused: 'bg-yellow-100 text-yellow-800'
    };
    return badges?.[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Campañas Activas</p>
              <p className="text-2xl font-bold text-foreground">{activeCampaigns?.length}</p>
            </div>
            <Icon name="Activity" size={32} className="text-green-600" />
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Borradores</p>
              <p className="text-2xl font-bold text-foreground">{draftCampaigns?.length}</p>
            </div>
            <Icon name="FileText" size={32} className="text-gray-600" />
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Completadas</p>
              <p className="text-2xl font-bold text-foreground">{completedCampaigns?.length}</p>
            </div>
            <Icon name="CheckCircle" size={32} className="text-blue-600" />
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Campañas</p>
              <p className="text-2xl font-bold text-foreground">{campaigns?.length}</p>
            </div>
            <Icon name="BarChart3" size={32} className="text-primary" />
          </div>
        </div>
      </div>

      {/* Active Campaigns List */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <Icon name="Zap" size={24} className="mr-2 text-primary" />
            Campañas Activas
          </h2>
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={onRefresh}>
            Actualizar
          </Button>
        </div>

        {activeCampaigns?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No hay campañas activas</p>
            <p className="text-sm text-muted-foreground mt-2">Crea una nueva campaña para comenzar</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeCampaigns?.map((campaign) => (
              <div key={campaign?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <Icon
                      name={getPlatformIcon(campaign?.platform)}
                      size={24}
                      className={getPlatformColor(campaign?.platform)}
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{campaign?.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{campaign?.content}</p>
                      <div className="flex items-center space-x-4 mt-3">
                        <div className="flex items-center space-x-1">
                          <Icon name="Eye" size={16} className="text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">2.5K impresiones</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="MousePointer" size={16} className="text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">156 clics</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Users" size={16} className="text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">12 conversiones</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(campaign?.status)}`}>
                    {campaign?.status === 'active' ? 'Activa' : campaign?.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Campaigns Table */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
          <Icon name="List" size={24} className="mr-2 text-primary" />
          Todas las Campañas
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Nombre</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plataforma</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Estado</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Presupuesto</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Fecha Inicio</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {campaigns?.map((campaign) => (
                <tr key={campaign?.id} className="border-b border-border hover:bg-muted/50">
                  <td className="py-3 px-4 text-sm text-foreground">{campaign?.name}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <Icon
                        name={getPlatformIcon(campaign?.platform)}
                        size={18}
                        className={getPlatformColor(campaign?.platform)}
                      />
                      <span className="text-sm text-foreground capitalize">{campaign?.platform}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(campaign?.status)}`}>
                      {campaign?.status === 'active' ? 'Activa' : campaign?.status === 'draft' ? 'Borrador' : campaign?.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    ${campaign?.budget?.toLocaleString() || '0'}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {campaign?.startDate ? new Date(campaign?.startDate)?.toLocaleDateString() : '-'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="sm" iconName="MoreVertical" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignDashboard;
